import { useCallback, useEffect, useRef, useState } from 'react'
import { getThumbnailSrc } from './misc.tsx'

// Network connection types and their estimated speeds (Mbps)
const CONNECTION_SPEEDS = {
	'slow-2g': 0.5,
	'2g': 0.7,
	'3g': 1.5,
	'4g': 10,
	'5g': 50,
} as const

type ConnectionType = keyof typeof CONNECTION_SPEEDS

interface NetworkInfo {
	effectiveType: ConnectionType
	downlink: number
	rtt: number
}

interface ViewportInfo {
	width: number
	height: number
	devicePixelRatio: number
}

interface ThumbnailCacheEntry {
	url: string
	size: number
	timestamp: number
	preloaded: boolean
}

export class SmartThumbnailCache {
	private cache = new Map<string, ThumbnailCacheEntry>()
	private preloadQueue = new Set<string>()
	private debounceTimeouts = new Map<string, ReturnType<typeof setTimeout>>()
	private maxCacheSize = 100 // Maximum number of cached thumbnails
	private maxAge = 30 * 60 * 1000 // 30 minutes

	private getOptimalThumbnailSize(
		viewport: ViewportInfo,
		network: NetworkInfo,
	): number {
		const baseSize = Math.min(viewport.width, viewport.height) * 0.3
		const devicePixelSize = baseSize * viewport.devicePixelRatio

		// Adjust size based on network conditions
		const networkMultiplier = this.getNetworkMultiplier(network)
		const optimalSize = Math.round(devicePixelSize * networkMultiplier)

		// Clamp to reasonable bounds
		return Math.min(Math.max(optimalSize, 200), 800)
	}

	private getNetworkMultiplier(network: NetworkInfo): number {
		const baseSpeed = CONNECTION_SPEEDS[network.effectiveType] || 1.5
		const actualSpeed = Math.min(network.downlink || baseSpeed, baseSpeed * 2)

		if (actualSpeed < 1) return 0.6 // Low quality for very slow connections
		if (actualSpeed < 3) return 0.8 // Medium quality for slow connections
		return 1.0 // Full quality for fast connections
	}

	private shouldPreload(network: NetworkInfo): boolean {
		console.debug('ENV.DISABLE_SMART_CACHE', ENV.DISABLE_SMART_CACHE)
		if (ENV.DISABLE_SMART_CACHE) {
			return false
		}
		const speed = CONNECTION_SPEEDS[network.effectiveType] || 0.5
		const hasGoodConnection = speed >= 1.5 && network.downlink >= 2

		// Don't preload on touch-only devices (no hover capability)
		const hasPointerCapability = window.matchMedia(
			'(hover: hover) and (pointer: fine)',
		).matches

		return hasGoodConnection && hasPointerCapability
	}

	private cleanupOldEntries(): void {
		const now = Date.now()
		const entriesToDelete: string[] = []

		for (const [key, entry] of this.cache.entries()) {
			if (now - entry.timestamp > this.maxAge) {
				entriesToDelete.push(key)
			}
		}

		entriesToDelete.forEach((key) => this.cache.delete(key))

		// If still over limit, remove oldest entries
		if (this.cache.size > this.maxCacheSize) {
			const sortedEntries = Array.from(this.cache.entries()).sort(
				([, a], [, b]) => a.timestamp - b.timestamp,
			)

			const toRemove = sortedEntries.slice(
				0,
				this.cache.size - this.maxCacheSize,
			)
			toRemove.forEach(([key]) => this.cache.delete(key))
		}
	}

	getThumbnailUrl(
		captureId: string,
		frameId: string,
		viewport: ViewportInfo,
		network: NetworkInfo,
	): string {
		const optimalSize = this.getOptimalThumbnailSize(viewport, network)
		const cacheKey = `${captureId}-${frameId}-${optimalSize}`

		// Check if we have a cached entry
		const cached = this.cache.get(cacheKey)
		if (cached) {
			cached.timestamp = Date.now() // Update access time
			return cached.url
		}

		// Generate new URL and cache it
		const url = getThumbnailSrc(captureId, frameId, optimalSize)
		this.cache.set(cacheKey, {
			url,
			size: optimalSize,
			timestamp: Date.now(),
			preloaded: false,
		})

		this.cleanupOldEntries()
		return url
	}

	preloadThumbnail(
		captureId: string,
		frameId: string,
		viewport: ViewportInfo,
		network: NetworkInfo,
		signal?: AbortSignal,
	): void {
		if (!this.shouldPreload(network)) return

		const optimalSize = this.getOptimalThumbnailSize(viewport, network)
		const cacheKey = `${captureId}-${frameId}-${optimalSize}`

		if (this.preloadQueue.has(cacheKey) || this.cache.has(cacheKey)) return
		if (signal?.aborted) return

		this.preloadQueue.add(cacheKey)
		const url = getThumbnailSrc(captureId, frameId, optimalSize)

		const img = new Image()

		const cleanup = () => {
			this.preloadQueue.delete(cacheKey)
		}

		img.onload = () => {
			if (signal?.aborted) return
			cleanup()
			this.cache.set(cacheKey, {
				url,
				size: optimalSize,
				timestamp: Date.now(),
				preloaded: true,
			})
		}

		img.onerror = cleanup

		if (signal) {
			signal.addEventListener('abort', cleanup, { once: true })
		}

		img.src = url
	}

	schedulePreload(
		captureId: string,
		frameId: string,
		viewport: ViewportInfo,
		network: NetworkInfo,
		delay: number,
		signal?: AbortSignal,
	): void {
		if (signal?.aborted) return

		const timeoutId = setTimeout(() => {
			if (signal?.aborted) return
			this.preloadThumbnail(captureId, frameId, viewport, network, signal)
		}, delay)

		// Cancel timeout if aborted
		signal?.addEventListener(
			'abort',
			() => {
				clearTimeout(timeoutId)
			},
			{ once: true },
		)
	}

	debouncedPreload(
		captureId: string,
		frameId: string,
		viewport: ViewportInfo,
		network: NetworkInfo,
		debounceMs: number = 150,
		signal?: AbortSignal,
	): void {
		if (signal?.aborted) return

		const debounceKey = `${captureId}-${frameId}`

		// Clear existing debounce timeout for this frame
		const existingTimeout = this.debounceTimeouts.get(debounceKey)
		if (existingTimeout) {
			clearTimeout(existingTimeout)
		}

		// Set new debounced preload
		const timeoutId = setTimeout(() => {
			if (signal?.aborted) return
			this.debounceTimeouts.delete(debounceKey)
			this.preloadThumbnail(captureId, frameId, viewport, network, signal)
		}, debounceMs)

		this.debounceTimeouts.set(debounceKey, timeoutId)

		// Cancel timeout if aborted
		signal?.addEventListener(
			'abort',
			() => {
				const timeout = this.debounceTimeouts.get(debounceKey)
				if (timeout) {
					clearTimeout(timeout)
					this.debounceTimeouts.delete(debounceKey)
				}
			},
			{ once: true },
		)
	}

	getCacheStats() {
		const preloadedCount = Array.from(this.cache.values()).filter(
			(entry) => entry.preloaded,
		).length
		return {
			total: this.cache.size,
			preloaded: preloadedCount,
			queued: this.preloadQueue.size,
			maxSize: this.maxCacheSize,
		}
	}
}

// Global cache instance
const smartCache = new SmartThumbnailCache()

export function useNetworkInfo(): NetworkInfo | null {
	const [networkInfo, setNetworkInfo] = useState<NetworkInfo | null>(null)

	useEffect(() => {
		const updateNetworkInfo = () => {
			const nav = navigator as any
			const connection =
				nav.connection || nav.mozConnection || nav.webkitConnection

			if (connection) {
				setNetworkInfo({
					effectiveType: (connection.effectiveType || '4g') as ConnectionType,
					downlink: connection.downlink || 10,
					rtt: connection.rtt || 100,
				})
			} else {
				// Fallback for browsers without Network Information API
				setNetworkInfo({
					effectiveType: '4g',
					downlink: 10,
					rtt: 100,
				})
			}
		}

		updateNetworkInfo()

		const nav = navigator as any
		const connection =
			nav.connection || nav.mozConnection || nav.webkitConnection
		if (connection && 'addEventListener' in connection) {
			connection.addEventListener('change', updateNetworkInfo)
			return () => connection.removeEventListener('change', updateNetworkInfo)
		}
	}, [])

	return networkInfo
}

export function useViewport(): ViewportInfo | null {
	const [viewport, setViewport] = useState<ViewportInfo | null>(null)

	useEffect(() => {
		const updateViewport = () => {
			setViewport({
				width: window.innerWidth,
				height: window.innerHeight,
				devicePixelRatio: window.devicePixelRatio || 1,
			})
		}

		updateViewport()
		window.addEventListener('resize', updateViewport)
		return () => window.removeEventListener('resize', updateViewport)
	}, [])

	return viewport
}

export function useSmartThumbnailCache() {
	const networkInfo = useNetworkInfo()
	const viewport = useViewport()
	const abortControllerRef = useRef<AbortController | undefined>(undefined)

	// Create AbortController on mount
	useEffect(() => {
		abortControllerRef.current = new AbortController()

		return () => {
			// Only abort this component's requests on unmount
			abortControllerRef.current?.abort()
		}
	}, [])

	const schedulePreload = useCallback(
		(targetCaptureId: string, targetFrameId: string, delay: number) => {
			if (!networkInfo || !viewport) return
			smartCache.schedulePreload(
				targetCaptureId,
				targetFrameId,
				viewport,
				networkInfo,
				delay,
				abortControllerRef.current?.signal,
			)
		},
		[networkInfo, viewport],
	)

	const debouncedPreload = useCallback(
		(targetCaptureId: string, targetFrameId: string, debounceMs?: number) => {
			if (!networkInfo || !viewport) return
			smartCache.debouncedPreload(
				targetCaptureId,
				targetFrameId,
				viewport,
				networkInfo,
				debounceMs,
				abortControllerRef.current?.signal,
			)
		},
		[networkInfo, viewport],
	)

	const getSmartThumbnailUrl = useCallback(
		(targetCaptureId: string, targetFrameId: string) => {
			if (!networkInfo || !viewport)
				return getThumbnailSrc(targetCaptureId, targetFrameId, 400)
			return smartCache.getThumbnailUrl(
				targetCaptureId,
				targetFrameId,
				viewport,
				networkInfo,
			)
		},
		[networkInfo, viewport],
	)

	return {
		schedulePreload,
		debouncedPreload,
		getSmartThumbnailUrl,
		networkInfo,
		viewport,
	}
}

export { smartCache }
